/**
 * web/src/screens/Notebook/giftflow/api.ts — typed calls to the
 * /api/gift-search surface (worker/src/api/gift-search.ts, card
 * NYJEJRCBfBGQ). The giver is always the session member on the server; these
 * helpers never send one.
 */
import { api } from '../../../shell/bridge';
import type { GiftBrief, GiftPlan, GiftSearch, GiftSearchRec } from './types';

type ApiOpts = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: Record<string, unknown>;
  signal?: AbortSignal;
  timeoutMs?: number;
};

/** api() throws only the raw response body, so a failed call is unactionable
 *  from the wall text alone ({"status":500} tells nobody anything). Log the
 *  request line here, then rethrow the original body so callers can still
 *  parse it (card Y4ujYPz2zk2K). */
async function req<T>(path: string, opts?: ApiOpts): Promise<T> {
  try {
    return await api<T>(path, opts);
  } catch (e) {
    console.error(`gift-flow: ${opts?.method ?? 'GET'} ${path} failed — ${e instanceof Error ? e.message : String(e)}`);
    throw e;
  }
}

type ParsedInput = {
  kind?: string | null;
  budgetMin?: number | null;
  budgetMax?: number | null;
  currency?: string | null;
  vibe?: string | null;
  avoid?: string[];
  timing?: string | null;
  surpriseLevel?: string | null;
};

export const giftSearchApi = {
  list: () => req<{ searches: GiftSearch[] }>('/api/gift-search'),
  create: (body: { recipientId: string; occasionType: string; occasionDate?: string | null; status?: string }) =>
    req<{ search: GiftSearch }>('/api/gift-search', { method: 'POST', body }),
  get: (searchId: string) => req<{ search: GiftSearch }>(`/api/gift-search/${encodeURIComponent(searchId)}`),
  update: (searchId: string, patch: Record<string, unknown>) =>
    req<{ search: GiftSearch }>(`/api/gift-search/${encodeURIComponent(searchId)}`, { method: 'PATCH', body: patch }),
  saveBrief: (searchId: string, input: { freeText?: string; parsed?: ParsedInput }) =>
    req<{ brief: GiftBrief }>(`/api/gift-search/${encodeURIComponent(searchId)}/brief`, { method: 'POST', body: input }),
  brief: (searchId: string, version?: number) =>
    req<{ brief: GiftBrief }>(`/api/gift-search/${encodeURIComponent(searchId)}/brief${version !== undefined ? `?version=${version}` : ''}`),
  briefs: (searchId: string) =>
    req<{ briefs: GiftBrief[] }>(`/api/gift-search/${encodeURIComponent(searchId)}/briefs`),
  addRec: (searchId: string, input: Record<string, unknown>) =>
    req<{ rec: GiftSearchRec }>(`/api/gift-search/${encodeURIComponent(searchId)}/recs`, { method: 'POST', body: input }),
  recs: (searchId: string) =>
    req<{ recs: GiftSearchRec[] }>(`/api/gift-search/${encodeURIComponent(searchId)}/recs`),
  rec: (recId: string) => req<{ rec: GiftSearchRec }>(`/api/gift-search/recs/${encodeURIComponent(recId)}`),
  updateRec: (recId: string, patch: Record<string, unknown>) =>
    req<{ rec: GiftSearchRec }>(`/api/gift-search/recs/${encodeURIComponent(recId)}`, { method: 'PATCH', body: patch }),
  createPlan: (searchId: string, body: Record<string, unknown>) =>
    req<{ plan: GiftPlan }>(`/api/gift-search/${encodeURIComponent(searchId)}/plan`, { method: 'POST', body }),
  plans: (searchId: string) =>
    req<{ plans: GiftPlan[] }>(`/api/gift-search/${encodeURIComponent(searchId)}/plans`),
  planForRec: (recId: string) =>
    req<{ plan: GiftPlan }>(`/api/gift-search/recs/${encodeURIComponent(recId)}/plan`),
  updatePlan: (planId: string, patch: Record<string, unknown>) =>
    req<{ plan: GiftPlan }>(`/api/gift-search/plans/${encodeURIComponent(planId)}`, { method: 'PATCH', body: patch }),
};
